// src/components/layout/Navigation.js
import React, { useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Button,
    IconButton,
    Container,
    Badge,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    Menu,
    MenuItem,
    Avatar,
    Chip,
    useTheme,
    useMediaQuery,
} from '@mui/material'
import {
    Language,
    NotificationsNone,
    PersonOutline,
    Logout as LogoutIcon,
    AccountCircleOutlined,
    Menu as MenuIcon,
    Close as CloseIcon
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import { useNotification } from '../../contexts/NotificationContext'
import { useWalletSafe } from '../../contexts/WalletContext'
import mojoLogo from '../../assets/brand/MojoPayr.png'

const ORANGE = '#FF8A00'

export default function Navigation() {
    const { user, logout } = useAuth()
    const { lang, setLanguage, t } = useLanguage()
    const { unreadCount, markAllRead } = useNotification()
    const wallet = useWalletSafe()
    const navigate = useNavigate()
    const location = useLocation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [languageAnchor, setLanguageAnchor] = useState(null)
    const [accountAnchor, setAccountAnchor] = useState(null)

    const isAuthPage =
        location.pathname === '/login' || location.pathname === '/register'

    const userName = user?.name || 'MojoPayr User'
    const userInitials = userName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('') || 'MP'
    const role = String(user?.role || user?.type || user?.account_type || '').toLowerCase()
    const isMerchant =
        role === 'merchant' ||
        role === 'business' ||
        user?.is_business === true ||
        !!user?.business_name
    const isServicePro = String(user?.business_kind || user?.merchant_type || '').toLowerCase() === 'service_pro'
    const isGovernment = role === 'government' || !!user?.agencyName
    const countryCode = String(user?.country || '').toUpperCase()
    const diasporaCurrencyByCountry = { US: 'USD', USA: 'USD', CA: 'CAD', GB: 'GBP', UK: 'GBP', AU: 'AUD', FR: 'EUR', EU: 'EUR' }
    const localCurrencyByCountry = { SL: 'SLE', LR: 'USD', GN: 'GNF', GM: 'GMD' }
    const walletCurrency = diasporaCurrencyByCountry[countryCode] || localCurrencyByCountry[countryCode] || String(user?.baseCurrency || 'USD').toUpperCase()
    const walletBalance = Number(wallet?.getBalance?.(walletCurrency) || 0)
    const lowWalletReminder =
        user && !isGovernment
            ? (diasporaCurrencyByCountry[countryCode] ? walletBalance < 50 : walletBalance <= 0)
            : false
    const bellCount = Number(unreadCount || 0) + (lowWalletReminder ? 1 : 0)

    const merchantToolMeta = {
        '/invoices/create': {
            en: 'Create Invoice',
            fr: 'Creer facture',
        },
        '/merchant/scan-ticket': {
            en: 'Scan Ticket',
            fr: 'Scanner billet',
        },
        '/merchant/pos': {
            en: 'POS Mode',
            fr: 'Mode POS',
        },
        '/merchant/receive-qr': {
            en: 'Generate QR',
            fr: 'Generer QR',
        },
        '/merchant/create-event': {
            en: 'Create Event',
            fr: 'Creer evenement',
        },
        '/merchant/sell-airtime': {
            en: 'Sell Airtime',
            fr: 'Vendre airtime',
        },
        '/merchant/business-tax': {
            en: 'Business Tax',
            fr: 'Taxe entreprise',
        },
        '/merchant/payroll-workforce': {
            en: 'Payroll & Workforce',
            fr: 'Paie et personnel',
        },
    }

    const activeMerchantTool = merchantToolMeta[location.pathname]
    const merchantToolItem = activeMerchantTool
        ? {
            path: location.pathname,
            label: lang === 'fr' ? activeMerchantTool.fr : activeMerchantTool.en,
        }
        : {
            path: '/invoices/create',
            label: lang === 'fr' ? 'Creer facture' : 'Create Invoice',
        }

    const primaryNavItems = isGovernment
        ? [
            { path: '/government/dashboard', label: lang === 'fr' ? 'Recettes fiscales' : 'Tax Collections' },
            { path: '/help-center', label: lang === 'fr' ? 'Centre d aide' : 'Help Center' },
        ]
        : isMerchant
        ? [
            { path: '/invoices', label: lang === 'fr' ? 'Tableau entreprise' : 'Business Dashboard' },
            ...(isServicePro ? [{ path: '/service-pros/manage', label: lang === 'fr' ? 'Espace Service Pro' : 'Service Pro Workspace' }] : []),
            merchantToolItem,
            { path: '/transactions', label: t('nav.transactions', null, 'Transactions') },
            { path: '/help-center', label: lang === 'fr' ? 'Centre d aide' : 'Help Center' },
        ]
        : [
            { path: '/dashboard', label: t('nav.dashboard', null, 'Dashboard') },
            { path: '/send', label: t('nav.send', null, 'Send Money') },
            { path: '/transactions', label: t('nav.transactions', null, 'Transactions') },
            { path: '/help-center', label: lang === 'fr' ? 'Centre d aide' : 'Help Center' },
        ]

    const openDrawer = () => setDrawerOpen(true)
    const closeDrawer = () => setDrawerOpen(false)
    const openLanguageMenu = (event) => setLanguageAnchor(event.currentTarget)
    const closeLanguageMenu = () => setLanguageAnchor(null)
    const openAccountMenu = (event) => setAccountAnchor(event.currentTarget)
    const closeAccountMenu = () => setAccountAnchor(null)
    const notificationsPath = isGovernment ? '/government/notifications' : '/notifications'
    const notificationsLabel = isGovernment
        ? (lang === 'fr' ? 'Notifications fiscales' : 'Tax Notifications')
        : 'Notifications'
    const notificationsDescription = isGovernment
        ? (lang === 'fr'
            ? 'Recus et alertes de votre autorite'
            : 'Receipts and alerts for your authority')
        : 'Receipts, alerts, and account activity'

    const handleLanguageChange = (nextLanguage) => {
        setLanguage(nextLanguage)
        closeLanguageMenu()
    }

    const handleAccountNavigate = async (path) => {
        closeAccountMenu()
        if (path === notificationsPath && !isGovernment) {
            await markAllRead()
        }
        navigate(path)
    }

    const handleNotificationsClick = async (event) => {
        event.stopPropagation()
        closeAccountMenu()
        if (!isGovernment) {
            await markAllRead()
        }
        navigate(notificationsPath)
    }

    const handleLogout = () => {
        closeAccountMenu()
        logout()
    }

    const navItem = (path, label) => {
        const isActive = location.pathname === path

        return (
            <Box
                component={NavLink}
                to={path}
                key={path}
                sx={{
                    textDecoration: 'none',
                    color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.75)',
                    fontWeight: 800,
                    fontSize: { md: 13, lg: 14 },
                    px: { md: 1.3, lg: 2 },
                    py: 1,
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.25s ease',
                    backgroundColor: isActive
                        ? 'rgba(255,138,0,0.12)'
                        : 'transparent',
                    border: isActive
                        ? '1px solid rgba(255,138,0,0.45)'
                        : '1px solid transparent',
                    boxShadow: isActive
                        ? '0 0 14px rgba(255,138,0,0.25)'
                        : 'none',
                    '&:hover': {
                        color: '#FFFFFF',
                        backgroundColor: 'rgba(255,138,0,0.10)',
                        border: '1px solid rgba(255,138,0,0.6)',
                        boxShadow: '0 0 18px rgba(255,138,0,0.35)',
                        transform: 'translateY(-2px)',
                    },
                }}
            >
                {label}
            </Box>
        )
    }

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                backgroundColor: 'rgba(8,12,20,0.75)',
                backdropFilter: 'blur(18px)',
                borderBottom: '1px solid rgba(255,138,0,0.08)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
            }}
        >
            <Container maxWidth="lg">
                <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 76 } }}>

                    {/* Logo */}
                    <Box
                        onClick={() => navigate('/')}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.2,
                            mr: 2,
                        }}
                    >
                        <Box
                            component="img"
                            src={mojoLogo}
                            alt="MojoPayr"
                            sx={{ width: 28, height: 28 }}
                        />
                        <Typography
                            sx={{
                                fontWeight: 950,
                                fontSize: 18,
                                color: '#FFFFFF',
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            MojoPayr
                        </Typography>
                    </Box>

                    {/* Center Navigation */}
                    {!isAuthPage && !isMobile && (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                gap: { md: 0.75, lg: 1.5 },
                            }}
                        >
                            {primaryNavItems.map((item) => navItem(item.path, item.label))}
                        </Box>
                    )}

                    {/* Right Side */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.8, md: 1.2 },
                            ml: 'auto',
                        }}
                    >
                        {!isAuthPage && (
                            <>
                                <Button
                                    onClick={openLanguageMenu}
                                    startIcon={<Language />}
                                    endIcon={
                                        <Typography
                                            component="span"
                                            sx={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.8 }}
                                        >
                                            {lang === 'fr' ? 'FR' : 'EN'}
                                        </Typography>
                                    }
                                    sx={{
                                        minWidth: 0,
                                        textTransform: 'none',
                                        color: '#FFFFFF',
                                        borderRadius: 999,
                                        px: { xs: 1, md: 1.4 },
                                        py: 0.8,
                                        border: '1px solid rgba(255,255,255,0.14)',
                                        bgcolor: 'rgba(255,255,255,0.05)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,138,0,0.08)',
                                            borderColor: 'rgba(255,138,0,0.35)',
                                        },
                                    }}
                                >
                                    {!isMobile && (lang === 'fr' ? 'Francais' : 'English')}
                                </Button>

                                {user ? (
                                    <>
                                    <Button
                                        onClick={openAccountMenu}
                                        startIcon={
                                            <Avatar
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    bgcolor: 'rgba(255,138,0,0.18)',
                                                    color: ORANGE,
                                                    fontSize: 12,
                                                    fontWeight: 900,
                                                    border: '1px solid rgba(255,138,0,0.3)',
                                                }}
                                            >
                                                {userInitials}
                                            </Avatar>
                                        }
                                        sx={{
                                            maxWidth: { xs: 56, md: 240 },
                                            textTransform: 'none',
                                            justifyContent: 'space-between',
                                            color: '#FFFFFF',
                                            borderRadius: 999,
                                            px: { xs: 0.8, md: 1.4 },
                                            py: 0.8,
                                            border: '1px solid rgba(255,255,255,0.14)',
                                            bgcolor: 'rgba(255,255,255,0.05)',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,138,0,0.08)',
                                                borderColor: 'rgba(255,138,0,0.35)',
                                            },
                                        }}
                                    >
                                        {!isMobile && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 900,
                                                        color: '#FFFFFF',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: 112,
                                                    }}
                                                >
                                                    {userName}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 11,
                                                        color: 'rgba(255,255,255,0.55)',
                                                        lineHeight: 1.1,
                                                    }}
                                                >
                                                    {isGovernment
                                                        ? (lang === 'fr' ? 'Agence' : 'Agency')
                                                        : t('nav.account', null, 'Account')}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Button>
                                    <IconButton
                                        aria-label={notificationsLabel}
                                        onClick={handleNotificationsClick}
                                        sx={{
                                            color: '#FFFFFF',
                                            border: '1px solid rgba(255,255,255,0.14)',
                                            bgcolor: 'rgba(255,255,255,0.05)',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,138,0,0.08)',
                                                borderColor: 'rgba(255,138,0,0.35)',
                                            },
                                        }}
                                    >
                                        <Badge badgeContent={bellCount} color="error">
                                            <NotificationsNone sx={{ fontSize: 20 }} />
                                        </Badge>
                                    </IconButton>
                                    </>
                                ) : null}
                            </>
                        )}

                        {!user && (
                            <>
                                {!isMobile && (
                                    <Button
                                        onClick={() => navigate('/login')}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: 999,
                                            fontWeight: 900,
                                            color: '#FFFFFF',
                                            border: '1px solid rgba(255,255,255,0.18)',
                                            bgcolor: 'rgba(255,255,255,0.05)',
                                            px: 2,
                                            transition: 'all 0.25s ease',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,138,0,0.08)',
                                                border: '1px solid rgba(255,138,0,0.65)',
                                                boxShadow: '0 0 18px rgba(255,138,0,0.35)',
                                            },
                                        }}
                                    >
                                        {t('nav.login', null, 'Login')}
                                    </Button>
                                )}

                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/register')}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: 999,
                                        fontWeight: 950,
                                        bgcolor: ORANGE,
                                        color: '#0B0620',
                                        px: 2.4,
                                        transition: 'all 0.25s ease',
                                        '&:hover': {
                                            bgcolor: '#ff9c2a',
                                            boxShadow: '0 0 22px rgba(255,138,0,0.45)',
                                        },
                                    }}
                                >
                                    {t('nav.signup', null, 'Sign Up')}
                                </Button>
                            </>
                        )}

                        {!isAuthPage && isMobile && (
                            <IconButton
                                onClick={openDrawer}
                                sx={{
                                    color: '#FFFFFF',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    bgcolor: 'rgba(255,255,255,0.04)',
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </Container>

            <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={closeLanguageMenu}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 180,
                        bgcolor: '#141B2D',
                        color: '#FFFFFF',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 3,
                        boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
                    },
                }}
            >
                <MenuItem onClick={() => handleLanguageChange('en')}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, width: '100%' }}>
                        <Typography sx={{ flex: 1, fontWeight: 700 }}>English</Typography>
                        {lang === 'en' && <Chip label="Default" size="small" sx={{ bgcolor: 'rgba(255,138,0,0.18)', color: ORANGE, fontWeight: 800 }} />}
                    </Box>
                </MenuItem>
                <MenuItem onClick={() => handleLanguageChange('fr')}>
                    <Typography sx={{ fontWeight: 700 }}>Francais</Typography>
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={accountAnchor}
                open={Boolean(accountAnchor)}
                onClose={closeAccountMenu}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 250,
                        bgcolor: '#141B2D',
                        color: '#FFFFFF',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 3,
                        boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
                        overflow: 'hidden',
                    },
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.6,
                        background: 'linear-gradient(135deg, rgba(92,45,255,0.35), rgba(255,138,0,0.16))',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <Typography sx={{ fontWeight: 900, color: '#FFFFFF' }}>{userName}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                        {user?.email || t('nav.account', null, 'Account')}
                    </Typography>
                </Box>
                <MenuItem onClick={() => handleAccountNavigate('/profile')}>
                    <ListItemIcon>
                        <PersonOutline sx={{ color: ORANGE }} />
                    </ListItemIcon>
                    <ListItemText
                        primary={t('nav.profile', null, 'Profile')}
                        secondary="Identity and account details"
                        secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }}
                    />
                </MenuItem>
                <MenuItem onClick={() => handleAccountNavigate(notificationsPath)}>
                    <ListItemIcon>
                        <NotificationsNone sx={{ color: ORANGE }} />
                    </ListItemIcon>
                    <ListItemText
                        primary={notificationsLabel}
                        secondary={notificationsDescription}
                        secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }}
                    />
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon sx={{ color: ORANGE }} />
                    </ListItemIcon>
                    <ListItemText primary={t('nav.logout', null, 'Logout')} />
                </MenuItem>
            </Menu>

            {/* Mobile Drawer */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={closeDrawer}
                PaperProps={{
                    sx: {
                        width: 'min(86vw, 320px)',
                        bgcolor: '#0B1020',
                        color: '#FFFFFF',
                        borderRight: '1px solid rgba(255,255,255,0.08)',
                    },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="img" src={mojoLogo} sx={{ width: 28, height: 28 }} />
                        <Typography sx={{ fontWeight: 950 }}>
                            MojoPayr
                        </Typography>
                    </Box>
                    <IconButton onClick={closeDrawer}>
                        <CloseIcon sx={{ color: '#FFFFFF' }} />
                    </IconButton>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                {!isAuthPage && (
                    <List sx={{ py: 1 }}>
                        {user && (
                            <>
                                <ListItemButton onClick={() => { closeDrawer(); navigate('/profile') }}>
                                    <ListItemIcon>
                                        <AccountCircleOutlined sx={{ color: ORANGE }} />
                                    </ListItemIcon>
                                    <ListItemText primary={userName} secondary={t('nav.profile', null, 'Profile')} />
                                </ListItemButton>
                                <ListItemButton onClick={async () => { closeDrawer(); if (!isGovernment) await markAllRead(); navigate(notificationsPath) }}>
                                    <ListItemIcon>
                                        <Badge badgeContent={bellCount} color="error">
                                            <NotificationsNone sx={{ color: ORANGE }} />
                                        </Badge>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={notificationsLabel}
                                        secondary={isGovernment
                                            ? (lang === 'fr' ? 'Recus et alertes fiscales' : 'Tax receipts and alerts')
                                            : 'Receipts and alerts'}
                                    />
                                </ListItemButton>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1 }} />
                            </>
                        )}
                        {primaryNavItems.map((item) => (
                            <ListItemButton key={item.path} onClick={() => { closeDrawer(); navigate(item.path) }}>
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        ))}
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1 }} />
                        <ListItemButton onClick={() => handleLanguageChange(lang === 'en' ? 'fr' : 'en')}>
                            <ListItemIcon>
                                <Language sx={{ color: ORANGE }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={lang === 'en' ? 'Switch to French' : 'Passer en anglais'}
                                secondary={lang === 'en' ? 'English is default' : 'Francais actif'}
                            />
                        </ListItemButton>
                        {user ? (
                            <ListItemButton onClick={handleLogout}>
                                <ListItemIcon>
                                    <LogoutIcon sx={{ color: ORANGE }} />
                                </ListItemIcon>
                                <ListItemText primary={t('nav.logout', null, 'Logout')} />
                            </ListItemButton>
                        ) : null}
                    </List>
                )}
            </Drawer>
        </AppBar>
    )
}

